import * as XLSX from 'xlsx';
import { format, utcToZonedTime } from 'date-fns-tz';
import { FaRegFileExcel } from 'react-icons/fa';

// Tipagem para os dados que você está usando
interface DataProps {
  title?: string;
  description?: string;
  result?: string;
  prefixo?: string;
  start?: string;
  end?: string;
  status?: string;
  points?: number;
  indicator?: { nome?: string };
  participants_approval?: { status?: string };
  nec?: {
    cd_alcc?: string;
    nm_alcc?: string;
    nm_uor_sltt?: string;
  };
  target?: Array<{
    _id: string;
    name: string;
    extra?: {
      nome_guerra?: string;
      funcao?: string;
      prefixo?: string;
      nome_dependencia?: string;
    };
  }>;
  feedback?: Array<{
    _id: string;
    etapa: number;
    message: string;
    evaluatorId: string;
    evaluator_nome_guerra: string;
    evaluator_nome?: string;
  }>;
}

export const TaskLevelLabel: any = {
  aguardando: 'Aguardando validação',
  rejeitado: 'Devolvida',
  aprovado: 'Validada',
  concluido: 'Finalizada',
  concluidoAuto: 'Finalizada',
  apreciacaoExpirada: 'Prazo expirado',
  promovido: 'Indicada ao próximo nível',
  dobrado: 'Bonificada',
  valeu: 'Bonificada Valeu',
  mandou: 'Bonificada Mandou Bem',
  selo: 'Bonificada UAU',
};

export function GetTaskLevelLabel(key: string) {
  if (TaskLevelLabel && TaskLevelLabel[key]) {
    return TaskLevelLabel[key];
  }
  return key;
}

function getFormattedDate(value: any) {
  try {
    return format(utcToZonedTime(value, 'GMT'), 'dd/MM/yy');
  } catch (e) {
    return '---';
  }
}

const points = (data: any) => {
  if (
    !data ||
    typeof data.multiplicador !== 'number' ||
    data.multiplicador <= 0 ||
    typeof data.status !== 'string' ||
    typeof data.stageNumber !== 'number' ||
    data.stageNumber < 1 ||
    data.stageNumber > 3
  ) {
    return 0;
  }

  let adicional = 0;

  if (
    ((data.status === 'promovido' || data.status === 'concluido') &&
      ((data.stageNumber === 2 && data.multiplicador === 2) || (data.stageNumber === 3 && data.multiplicador === 4))) ||
    (data.stageNumber === 3 && data.multiplicador === 8)
  ) {
    adicional = 100;
  }

  if (data.stageNumber === 3 && data.multiplicador === 16) {
    adicional = 200;
  }

  return data.multiplicador * 300 + adicional;
};

const getStatusApprovalParticipants: any = {
  autoApproved: 'Número de participantes liberados automaticamente',
  pending: 'Número de participantes pendentes de autorização',
  approved: 'Número de participantes aprovados',
  rejected: 'Número de participantes não aprovados',
};

// Função para exportar para Excel
const exportToExcel = (dataArray: any[]) => {
  // Criar array de dados formatados para a planilha
  const formattedData = dataArray.map((data, index) => ({
    Nº: index + 1,
    'Nome da Realização': data.title || '---',
    Descrição: data.description || '',
    Resultado: data.result || '',
    Prefixo: data.prefixo || '',
    'Data Início': data.start ? getFormattedDate(data.start) : '',
    'Data Fim': data.end ? getFormattedDate(data.end) : '',
    Pontos: points(data) || 0,
    Indicador: data.indicator?.nome || '',
    'Status Participação': getStatusApprovalParticipants[data.participants_approval?.status] || '',
    NEC: data.nec?.cd_alcc && data.nec?.nm_alcc ? `${data.nec.cd_alcc} - ${data.nec.nm_alcc}` : '',
    Demandante: data.nec?.nm_uor_sltt || '',
    Gestores:
      `${data.original_manager_detail?.nome_guerra} (${data.original_manager_detail?._id}), ` +
        data.evaluator_detail?.map((m: any) => `${m.nome_guerra} (${m._id})`).join(', ') || '',
    Participantes: data.target?.map((p: any) => `${p.name} (${p._id})`).join(', ') || '',
    Feedback: data.feedback?.map((f: any) => `${f.evaluator_nome_guerra}: ${f.message}`).join(' | ') || '',
  }));

  // Criar uma worksheet a partir dos dados
  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  // Criar um workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Realizações');

  // Ajustar largura das colunas (opcional)
  const colWidths = Object.keys(formattedData[0]).map(key => ({
    wch: Math.max(key.length, ...formattedData.map(item => String(item[key as keyof typeof item]).length)),
  }));
  worksheet['!cols'] = colWidths;

  // Exportar o arquivo
  XLSX.writeFile(workbook, 'realizacoes.xlsx');
};

// Função auxiliar para formatar números
const formatNumber = (value: number | undefined) => {
  return value ? Intl.NumberFormat('pt-BR').format(Math.ceil(value)) : '0';
};

// Função para exportar os dados para Excel
const exportToExcelUnidade = (dataArray: any[]) => {
  console.log(dataArray)
  // Mapear os dados para o formato da planilha
  const formattedData = dataArray.map((origin, index) => ({
    Nº: index + 1,
    'Nome da Realização': `${origin.titulo || '---'} - ${origin.faseTitle || '---'}`,
    Status: GetTaskLevelLabel(origin.status) || '---',
    'Grupo de Trabalho': origin.nome_uor || '---',
    Pontos: formatNumber(origin.total),
    Detalhes: origin.descricao || '---',
  }));

  // Criar uma worksheet a partir dos dados formatados
  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  // Criar um workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Tarefas');

  // Ajustar largura das colunas (opcional)
  const colWidths = Object.keys(formattedData[0]).map((key) => ({
    wch: Math.max(
      key.length,
      ...formattedData.map((item) => String(item[key as keyof typeof item]).length)
    ),
  }));
  worksheet['!cols'] = colWidths;

  // Exportar o arquivo
  XLSX.writeFile(workbook, 'tarefas.xlsx');
};

// Componente com botão de exportação
export function ExportButton(props: { data: any; unidade?: boolean }) {
  const { data, unidade } = props;

  return (
    <button
      onClick={() => unidade ? exportToExcelUnidade(data) : exportToExcel(data)}
      className="space-x-2 flex items-center text-electric-blue text-base lg:text-md uppercase hover:no-underline hover:text-white active:no-underline"
    >
      <span>Exportar para Excel</span>
      <FaRegFileExcel />
    </button>
  );
}
